"use client"

import { useEffect, useState } from "react"
import type React from "react"
import type { Dispatch, RefObject, SetStateAction } from "react"
import type { BooruPost } from "@/lib/booru-api"
import { contentTypeDetector } from "@/lib/content-type-detector"

// Moved out of components/booru-detail-modal.tsx: the same handler code, reading the same values from the modal's render.
// Pinch, touch-swipe and double-click state lived only here, so it moved too. Zoom and pan state stay in the modal
// (the keyboard shortcuts, the media view and the pan effect read them).
export interface MediaGesturesInput {
  allowVideoZoom: boolean
  canNavigate: { prev: boolean; next: boolean } | undefined
  currentAlbumPage: number
  enableMousePan: boolean
  handMode: "right" | "left"
  handleAlbumNextPage: () => void
  handleAlbumPrevPage: () => void
  imageDisplayMode: "contain" | "cover"
  imageNavDirection: "horizontal-next-right" | "horizontal-next-left" | "vertical-next-down" | "vertical-next-up"
  isAlbum: boolean
  isMediaVideo: boolean
  isPanning: boolean
  isVideoZoomEnabled: boolean
  longPressTimeoutRef: RefObject<NodeJS.Timeout | undefined>
  mediaRootRef: RefObject<HTMLDivElement | null>
  mouseWheelBehavior: "zoom" | "navigate" | "pan"
  onNavigate: ((direction: "prev" | "next") => void) | undefined
  onOpenChange: (open: boolean) => void
  panSensitivity: number
  panStart: { x: number; y: number }
  panX: number
  panY: number
  post: BooruPost | null
  setHideUI: Dispatch<SetStateAction<boolean>>
  setIsPanning: Dispatch<SetStateAction<boolean>>
  setPanStart: Dispatch<SetStateAction<{ x: number; y: number }>>
  setPanX: Dispatch<SetStateAction<number>>
  setPanY: Dispatch<SetStateAction<number>>
  setShowInfo: Dispatch<SetStateAction<boolean>>
  setShowSaveToGoonfolio: Dispatch<SetStateAction<boolean>>
  setZoom: Dispatch<SetStateAction<number>>
  showInfo: boolean
  totalAlbumPages: number
  viewingAdSlot: number | null
  zoom: number
}

export function useMediaGestures({
  allowVideoZoom,
  canNavigate,
  currentAlbumPage,
  enableMousePan,
  handMode,
  handleAlbumNextPage,
  handleAlbumPrevPage,
  imageDisplayMode,
  imageNavDirection,
  isAlbum,
  isMediaVideo,
  isPanning,
  isVideoZoomEnabled,
  longPressTimeoutRef,
  mediaRootRef,
  mouseWheelBehavior,
  onNavigate,
  onOpenChange,
  panSensitivity,
  panStart,
  panX,
  panY,
  post,
  setHideUI,
  setIsPanning,
  setPanStart,
  setPanX,
  setPanY,
  setShowInfo,
  setShowSaveToGoonfolio,
  setZoom,
  showInfo,
  totalAlbumPages,
  viewingAdSlot,
  zoom,
}: MediaGesturesInput) {
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 })
  const [touchEnd, setTouchEnd] = useState({ x: 0, y: 0 })
  const [isPinching, setIsPinching] = useState(false)
  const [lastClickTime, setLastClickTime] = useState(0)

  // Zoom toward the cursor position — keeps the point under the cursor stationary.
  // The transform is scale(z) translate(panX/z, panY/z), so panX/Y are in screen-space pixels.
  // Formula: newPan = cursor + (pan - cursor) * (newZoom / oldZoom)
  const zoomTowardCursor = (e: React.WheelEvent, oldZoom: number, newZoom: number) => {
    if (!mediaRootRef.current) return
    const rect = mediaRootRef.current.getBoundingClientRect()
    const cx = e.clientX - rect.left - rect.width / 2
    const cy = e.clientY - rect.top - rect.height / 2
    const ratio = newZoom / oldZoom
    setPanX((px) => cx + (px - cx) * ratio)
    setPanY((py) => cy + (py - cy) * ratio)
  }

  const handleWheel = (e: React.WheelEvent) => {
    // Check if the wheel event is happening within the info panel or any scrollable area
    const target = e.target as Element
    const isInScrollableArea = target.closest('[data-radix-scroll-area-viewport]') ||
                              target.closest('.overflow-y-auto') ||
                              target.closest('[role="dialog"][data-state="open"]')

    if (isInScrollableArea && showInfo) return

    // Determine effective behavior: always treat scroll as zoom unless user overrode to navigate/pan
    const behavior = mouseWheelBehavior

    if (isMediaVideo) {
      if (!isVideoZoomEnabled) return

      if (behavior === "pan" && zoom > 1) {
        e.preventDefault()
        setPanX((x) => x - e.deltaX * 0.5 * panSensitivity)
        setPanY((y) => y - e.deltaY * 0.5 * panSensitivity)
        return
      }

      e.preventDefault()
      const delta = e.deltaY < 0 ? 0.15 : -0.15
      const newZoom = Math.max(0.5, Math.min(4, zoom + delta))
      zoomTowardCursor(e, zoom, newZoom)
      setZoom(newZoom)
      return
    }

    if (behavior === "zoom") {
      e.preventDefault()
      const delta = e.deltaY < 0 ? 0.15 : -0.15
      const newZoom = Math.max(0.5, Math.min(4, zoom + delta))
      zoomTowardCursor(e, zoom, newZoom)
      setZoom(newZoom)
      return
    }

    if (behavior === "pan" && zoom > 1) {
      e.preventDefault()
      setPanX((x) => x - e.deltaX * 0.5 * panSensitivity)
      setPanY((y) => y - e.deltaY * 0.5 * panSensitivity)
      return
    }

    if (mouseWheelBehavior === "navigate") {
      if (!onNavigate) return

      const isHorizontal = imageNavDirection.startsWith("horizontal")
      const delta = isHorizontal ? e.deltaX : e.deltaY

      if (Math.abs(delta) > 10) {
        const shouldGoNext =
          (imageNavDirection === "horizontal-next-right" && delta > 0) ||
          (imageNavDirection === "horizontal-next-left" && delta < 0) ||
          (imageNavDirection === "vertical-next-down" && delta > 0) ||
          (imageNavDirection === "vertical-next-up" && delta < 0)

        console.log("[v0] Navigate wheel:", { shouldGoNext, delta, imageNavDirection })

        if (shouldGoNext && canNavigate?.next) {
          console.log("[v0] Going to next image")
          onNavigate("next")
        } else if (!shouldGoNext && canNavigate?.prev) {
          console.log("[v0] Going to prev image")
          onNavigate("prev")
        }
      }
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    // Check if the touch is happening within the info panel or any scrollable area
    const target = e.target as Element
    const isInScrollableArea = target.closest('[data-radix-scroll-area-viewport]') || 
                              target.closest('.overflow-y-auto') ||
                              target.closest('[role="dialog"][data-state="open"]') // Sheet content
    
    // If we're in a scrollable area (like the info panel), don't interfere with touch events
    if (isInScrollableArea && showInfo) {
      return // Allow normal touch scrolling
    }

    if (e.touches.length === 2) {
      if (!allowVideoZoom) return
      setIsPinching(true)
      const touch1 = e.touches[0]
      const touch2 = e.touches[1]
      const distance = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY)
      setPanStart({ x: distance, y: zoom })
      return
    }

    setTouchStart({ x: e.targetTouches[0].clientX, y: e.targetTouches[0].clientY })
    setTouchEnd({ x: e.targetTouches[0].clientX, y: e.targetTouches[0].clientY })

    // Start pan if zoomed OR in cover mode
    if ((zoom > 1 || imageDisplayMode === 'cover') && allowVideoZoom) {
      setIsPanning(true)
      setHideUI(true) // Hide UI when panning starts
      setPanStart({ x: panX, y: panY })
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    // Check if the touch is happening within the info panel or any scrollable area
    const target = e.target as Element
    const isInScrollableArea = target.closest('[data-radix-scroll-area-viewport]') || 
                              target.closest('.overflow-y-auto') ||
                              target.closest('[role="dialog"][data-state="open"]') // Sheet content
    
    // If we're in a scrollable area (like the info panel), don't interfere with touch events
    if (isInScrollableArea && showInfo) {
      return // Allow normal touch scrolling
    }

    if (e.touches.length === 2 && isPinching) {
      if (!allowVideoZoom) return
      e.preventDefault()
      const touch1 = e.touches[0]
      const touch2 = e.touches[1]
      const distance = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY)
      const initialDistance = panStart.x
      const zoomDelta = (distance / initialDistance) * panStart.y
      setZoom(Math.max(0.5, Math.min(5, zoomDelta)))
      return
    }

    if (isPanning && zoom > 1 && allowVideoZoom) {
      const deltaX = e.targetTouches[0].clientX - touchStart.x
      const deltaY = e.targetTouches[0].clientY - touchStart.y
      setPanX(panStart.x + deltaX * panSensitivity)
      setPanY(panStart.y + deltaY * panSensitivity)
    }

    setTouchEnd({ x: e.targetTouches[0].clientX, y: e.targetTouches[0].clientY })
  }

  const handleTouchEnd = () => {
    if (isPinching) {
      setIsPinching(false)
      return
    }

    if (isPanning) {
      setIsPanning(false)
      return
    }

    const deltaX = touchStart.x - touchEnd.x
    const deltaY = touchStart.y - touchEnd.y
    const absDeltaX = Math.abs(deltaX)
    const absDeltaY = Math.abs(deltaY)

    if (isAlbum && post?.albumImages && absDeltaX > 100 && absDeltaY < 50 && !(isMediaVideo && isVideoZoomEnabled)) {
      // Routed through the same handlers as the nav buttons so a swipe can
      // also land on a gallery ad slot instead of skipping straight past it.
      if (deltaX > 0 && (viewingAdSlot !== null || currentAlbumPage < totalAlbumPages - 1)) {
        handleAlbumNextPage()
        return
      } else if (deltaX < 0 && (viewingAdSlot !== null || currentAlbumPage > 0)) {
        handleAlbumPrevPage()
        return
      }
    }

    if (absDeltaY > 80 && absDeltaX > 80 && deltaY < 0) {
      onOpenChange(false)
      return
    }

    const infoDirection = handMode === "right" ? "left" : "right"

    if (infoDirection === "left" && deltaX > 50) {
      setShowInfo(true)
      return
    }
    if (infoDirection === "right" && deltaX < -50) {
      setShowInfo(true)
      return
    }

    const hideDirection = handMode === "right" ? "right" : "left"

    if (hideDirection === "right" && deltaX < -50) {
      setHideUI(true)
      return
    }
    if (hideDirection === "left" && deltaX > 50) {
      setHideUI(true)
      return
    }

    if (isMediaVideo && isVideoZoomEnabled) return

    if (!onNavigate) return

    const isHorizontal = imageNavDirection.startsWith("horizontal")
    const delta = isHorizontal ? deltaX : deltaY

    if (Math.abs(delta) > 50) {
      const shouldGoNext =
        (imageNavDirection === "horizontal-next-right" && delta > 0) ||
        (imageNavDirection === "horizontal-next-left" && delta < 0) ||
        (imageNavDirection === "vertical-next-down" && delta > 0) ||
        (imageNavDirection === "vertical-next-up" && delta < 0)

      if (shouldGoNext && canNavigate?.next) {
        onNavigate("next")
      } else if (!shouldGoNext && canNavigate?.prev) {
        onNavigate("prev")
      }
    }
  }

  const handleZoomIn = () => {
    setZoom((z) => Math.min(z + 0.25, 5))
  }

  const handleZoomOut = () => {
    setZoom((z) => Math.max(z - 0.25, 0.1))
  }

  const handleMediaLongPressStart = () => {
    let hasMoved = false
    let startX = 0
    let startY = 0

    const handleMouseMove = (e: MouseEvent) => {
      if (!hasMoved) {
        const deltaX = Math.abs(e.clientX - startX)
        const deltaY = Math.abs(e.clientY - startY)
        if (deltaX > 5 || deltaY > 5) {
          hasMoved = true
          if (longPressTimeoutRef.current) {
            clearTimeout(longPressTimeoutRef.current)
            longPressTimeoutRef.current = undefined
          }
        }
      }
      document.removeEventListener("mousemove", handleMouseMove)
    }

    const handleMouseDown = (e: MouseEvent) => {
      startX = e.clientX
      startY = e.clientY
    }

    document.addEventListener("mousedown", handleMouseDown, { once: true })
    document.addEventListener("mousemove", handleMouseMove)

    longPressTimeoutRef.current = setTimeout(() => {
      if (!hasMoved && !isPanning) {
        // Check if this content type should show the save-to-folio picker
        if (post && contentTypeDetector.shouldShowCollectionModal(post)) {
          setShowSaveToGoonfolio(true)
        } else {
          console.log('[BooruDetailModal] Save-to-folio suppressed for category/sub-category')
        }
      }
      document.removeEventListener("mousemove", handleMouseMove)
    }, 500)
  }

  const handleMediaLongPressEnd = () => {
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current)
      longPressTimeoutRef.current = undefined
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    console.log("[v0] Mouse down:", { zoom, enableMousePan, button: e.button, isPanning, imageDisplayMode })
    // Allow panning if zoomed OR in cover mode (for scrolling through tall images)
    if ((zoom > 1 || imageDisplayMode === 'cover') && e.button === 0 && enableMousePan && allowVideoZoom) {
      const currentTime = Date.now()
      if (currentTime - lastClickTime < 300 && zoom > 1) {
        // Double click detected - only reset zoom if actually zoomed
        setZoom(1)
        setPanX(0)
        setPanY(0)
        setLastClickTime(0)
        return
      }
      setLastClickTime(currentTime)

      e.preventDefault()
      console.log("[v0] Starting pan")
      setIsPanning(true)
      setHideUI(true) // Hide UI when panning starts
      setPanStart({ x: e.clientX, y: e.clientY })

      // Cancel long-press when starting to pan
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current)
        longPressTimeoutRef.current = undefined
      }
    } else {
      handleMediaLongPressStart()
    }
  }

  const handleMouseUp = () => {
    console.log("[v0] Mouse up:", { isPanning })
    if (isPanning) {
      setIsPanning(false)
    } else {
      handleMediaLongPressEnd()
    }
  }

  // Mouse-drag pan while the button is held (document-level listeners; the pan state itself lives in the modal).
  useEffect(() => {
    if (!isPanning) return

    let lastX = panStart.x
    let lastY = panStart.y

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (zoom > 1) {
        const deltaX = (e.clientX - lastX) * panSensitivity
        const deltaY = (e.clientY - lastY) * panSensitivity
        
        setPanX(prev => prev + deltaX)
        setPanY(prev => prev + deltaY)
        
        lastX = e.clientX
        lastY = e.clientY
      }
    }

    const handleGlobalMouseUp = () => {
      setIsPanning(false)
    }

    document.addEventListener("mousemove", handleGlobalMouseMove, { passive: true })
    document.addEventListener("mouseup", handleGlobalMouseUp)

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove)
      document.removeEventListener("mouseup", handleGlobalMouseUp)
    }
  }, [isPanning, panStart, zoom, panSensitivity])

  return {
    handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd, handleZoomIn, handleZoomOut,
    handleMediaLongPressEnd, handleMouseDown, handleMouseUp,
  }
}
